import React, { useState } from 'react';
import { Menu, MenuItem, Box, Portal } from '@mui/material';
import ChipComponent from './ChipComponent/ChipComponent';
import ConfirmationModal from '../Modal/ConfirmationModal';

/**
 * StatusSelector - A generic, reusable status selector component
 * 
 * Props:
 * - currentStatus: string (current status value)
 * - availableStatuses: array (list of available status options)
 * - getVariant: function (maps status to color variant)
 * - getIcon: function (maps status to icon)
 * - formatStatus: function (formats status text for display)
 * - onChange: function (callback when status changes)
 * - confirmChange: boolean (show confirmation modal, default: true)
 * - confirmTitle: string (confirmation modal title)
 * - confirmMessage: string (confirmation modal message)
 * - anchorEl: element (anchor element for menu positioning)
 * - onClose: function (callback when menu closes)
 */
const StatusSelector = ({
  currentStatus,
  availableStatuses = [],
  getVariant,
  getIcon,
  formatStatus = (status) => status,
  onChange,
  confirmChange = true,
  confirmTitle = "Confirm Status Change",
  confirmMessage = "Are you sure you want to change the status?",
  anchorEl,
  onClose,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleStatusClick = (status) => {
    if (confirmChange) {
      setSelectedStatus(status);
      // Close the menu first, then open modal
      onClose();
      // Small delay to ensure menu closes before modal opens
      setTimeout(() => {
        setIsConfirmModalOpen(true);
      }, 100);
    } else {
      onChange(status);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (selectedStatus) {
      onChange(selectedStatus);
    }
    setIsConfirmModalOpen(false);
    setSelectedStatus(null);
  };

  const handleCancel = () => {
    setIsConfirmModalOpen(false);
    setSelectedStatus(null);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        PaperProps={{
          style: {
            minWidth: '200px',
            marginTop: '8px',
          },
        }}
      >
        {availableStatuses.map((status) => {
          const isSelected = status === currentStatus;
          return (
            <MenuItem
              key={status}
              onClick={() => handleStatusClick(status)}
              selected={isSelected}
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '9px 17px',
                '&:hover': {
                  backgroundColor: '#f3f4f6',
                },
                '&.Mui-selected': {
                  backgroundColor: '#e5e7eb',  // Grey background for selected item
                  borderLeft: '3px solid #6b7280',  // Grey left border
                  '&:hover': {
                    backgroundColor: '#d1d5db',  // Darker grey on hover
                  },
                },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <ChipComponent
                  variant={getVariant ? getVariant(status) : 'default'}
                  icon={getIcon ? getIcon(status) : null}
                  size="small"
                  showTooltip={false}
                  sx={{ 
                    width: '100%',
                    minWidth: 'auto',
                  }}
                >
                  {formatStatus(status)}
                </ChipComponent>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      {confirmChange && (
        <Portal>
          <ConfirmationModal
            open={isConfirmModalOpen}
            onClose={handleCancel}
            onConfirm={handleConfirm}
            title={confirmTitle}
            message={confirmMessage}
          />
        </Portal>
      )}
    </>
  );
};

export default StatusSelector;

